import type { UpdateTenantProfileRequest } from '../api/types';
import { normalisePhone } from '../format';
import { useI18n } from '../i18n/I18nProvider';
import { EmailInput, Field, Input, PhoneInput, Textarea } from './Field';
import { Row, Stack } from './primitives';

/** The days of the week by the names the API stores a weekend in, in its week order (`DayOfWeek`). */
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/**
 * The days a nursery is closed every week (SRS §2.4). They decide who counts as absent, so they are chosen
 * rather than typed. The API refuses all seven, and keeps the days in week order whatever order they are ticked in.
 */
export function WeekendDaysPicker({ value, onChange }: { value: string[]; onChange: (days: string[]) => void }) {
  const { t } = useI18n();

  const toggle = (day: string, on: boolean) =>
    onChange(WEEKDAYS.filter((each) => (each === day ? on : value.includes(each))));

  return (
    // A group of choices, so a fieldset and a legend: a screen reader announces the group before its boxes.
    <fieldset className="dg-field">
      <legend className="dg-field__label">{t('identity.weekendDays')}</legend>
      <Row>
        {WEEKDAYS.map((day) => (
          <label key={day} className="dg-checkbox">
            <input
              type="checkbox"
              checked={value.includes(day)}
              onChange={(event) => toggle(day, event.target.checked)}
            />
            <span>{t(`weekday.${day}`)}</span>
          </label>
        ))}
      </Row>
      <p className="dg-field__hint">{t('identity.weekendDays.hint')}</p>
    </fieldset>
  );
}

/** What both the platform and the nursery itself edit of a nursery's identity (C7). */
export type TenantIdentityValues = UpdateTenantProfileRequest;

/** The identity fields of a nursery's record, the platform's profile or the nursery's own, to start an edit from. */
export function identityValuesOf(tenant: TenantIdentityValues): TenantIdentityValues {
  return {
    nameAr: tenant.nameAr,
    nameEn: tenant.nameEn,
    address: tenant.address,
    contactPhone: tenant.contactPhone,
    contactEmail: tenant.contactEmail,
    brandColor: tenant.brandColor,
    weekendDays: tenant.weekendDays,
  };
}

/**
 * The names, address, contact, colour and weekend of a nursery, as one set of fields. The platform's edit and
 * the nursery's own settings card show the same fields, so they read and refuse the same way.
 */
export function TenantIdentityFields({ value, onChange, errors = {} }: {
  value: TenantIdentityValues;
  onChange: (next: TenantIdentityValues) => void;
  /** Per-field messages from the API, by field name. */
  errors?: Record<string, string>;
}) {
  const { t } = useI18n();
  const set = <K extends keyof TenantIdentityValues>(key: K, next: TenantIdentityValues[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <Stack>
      <Field label={t('identity.nameAr')} error={errors.nameAr} required>
        {(props) => <Input {...props} value={value.nameAr} onChange={(e) => set('nameAr', e.target.value)} />}
      </Field>
      <Field label={t('identity.nameEn')} error={errors.nameEn}>
        {(props) => (
          <Input {...props} dir="ltr" value={value.nameEn ?? ''} onChange={(e) => set('nameEn', e.target.value)} />
        )}
      </Field>
      <Field label={t('identity.address')} error={errors.address}>
        {(props) => (
          <Textarea {...props} rows={2} value={value.address ?? ''} onChange={(e) => set('address', e.target.value)} />
        )}
      </Field>
      <Field label={t('identity.contactPhone')} hint={t('auth.phone.hint')} error={errors.contactPhone}>
        {(props) => (
          <PhoneInput {...props} value={value.contactPhone ?? ''} onChange={(e) => set('contactPhone', e.target.value)} />
        )}
      </Field>
      <Field label={t('identity.contactEmail')} error={errors.contactEmail}>
        {(props) => (
          <EmailInput {...props} value={value.contactEmail ?? ''} onChange={(e) => set('contactEmail', e.target.value)} />
        )}
      </Field>
      <Field label={t('identity.brandColor')} hint={t('identity.brandColor.hint')} error={errors.brandColor}>
        {(props) => (
          <Row>
            <Input
              {...props}
              dir="ltr"
              placeholder="#1A7F64"
              value={value.brandColor ?? ''}
              onChange={(e) => set('brandColor', e.target.value)}
            />
            <input
              type="color"
              aria-label={t('identity.brandColor.pick')}
              // A colour field cannot be empty, so it shows black until a colour is written or picked.
              value={/^#[0-9a-f]{6}$/i.test(value.brandColor ?? '') ? value.brandColor! : '#000000'}
              onChange={(e) => set('brandColor', e.target.value.toUpperCase())}
            />
          </Row>
        )}
      </Field>
      <WeekendDaysPicker value={value.weekendDays} onChange={(days) => set('weekendDays', days)} />
    </Stack>
  );
}

/**
 * The identity as the API takes it: text trimmed, an empty field as null, the phone in international form. A
 * number that cannot be read goes as typed, so the API says what is wrong with it rather than the field emptying.
 */
export function identityBody(values: TenantIdentityValues): TenantIdentityValues {
  const text = (value: string | null) => value?.trim() || null;
  const phone = text(values.contactPhone);
  return {
    nameAr: values.nameAr.trim(),
    nameEn: text(values.nameEn),
    address: text(values.address),
    contactPhone: phone && (normalisePhone(phone) ?? phone),
    contactEmail: text(values.contactEmail),
    brandColor: text(values.brandColor),
    weekendDays: values.weekendDays,
  };
}
